import { OptionalCardLink } from '@playbooks/molecules/cards/card-link';
import { Skeleton } from '@playbooks/molecules/skeletons';
import { Button } from '@playbooks/ui/components/button';
import type { CardProps as PrimitiveCardProps } from '@playbooks/ui/components/card';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@playbooks/ui/components/card';
import { CardMedia } from '@playbooks/ui/components/card';
import { Icon, resolveIcon } from '@playbooks/ui/components/icon';
import { cn } from '@playbooks/ui/lib/utils';

import type { CardButtonInteractionProps, CardContentProps, CardNavigationProps } from './types';

type PhotoCardProps = Pick<
	CardContentProps,
	'className' | 'footer' | 'icon' | 'loading' | 'media' | 'photo' | 'text' | 'title'
> &
	CardNavigationProps & { render?: PrimitiveCardProps['render'] };
type PhotoBtnCardProps = Omit<PhotoCardProps, 'render'> & CardButtonInteractionProps & { active?: boolean };

const PhotoCard = ({
	icon,
	photo,
	media: customMedia,
	title,
	text,
	loading,
	href,
	target,
	linkProps,
	className = '',
	footer,
	render,
}: PhotoCardProps) => {
	const linkHref = render ? undefined : href;
	const media =
		customMedia ||
		(photo ? (
			<img src={photo} alt={String(title)} />
		) : (
			<CardMedia>
				<Icon {...resolveIcon(icon, 'fad')} aria-hidden />
			</CardMedia>
		));

	// Theme
	const rootClassName = cn('flex-column h-full hover:border-border-strong hover:bg-surface-card-hover', className);

	// Render
	if (loading) return <Skeleton layout='photo' className={className} />;

	return (
		<Card render={render} className={rootClassName}>
			<CardContent className='text-center gap-y-1 mx-auto p-4'>
				<CardHeader className='aspect-[1/1] rounded-control flex-middle overflow-hidden mx-auto w-14 mb-2'>
					<OptionalCardLink
						variant='ghost'
						aria-label={String(title)}
						href={linkHref}
						target={target}
						{...linkProps}
						className='w-fit h-auto p-0'>
						{media}
					</OptionalCardLink>
				</CardHeader>
				{title && (
					<CardTitle>
						<OptionalCardLink
							variant='ghost'
							href={linkHref}
							target={target}
							{...linkProps}
							className='hover:underline h-auto p-0'>
							{title}
						</OptionalCardLink>
					</CardTitle>
				)}
				{text && <CardDescription className='line-clamp-2'>{text}</CardDescription>}
			</CardContent>
			{footer && <CardFooter className='px-4 pb-4'>{footer}</CardFooter>}
		</Card>
	);
};

export { PhotoCard };

export const PhotoBtnCard = ({ active = false, className = '', ...props }: PhotoBtnCardProps) => {
	// Theme
	const rootClassName = cn('group w-full h-auto p-0', className);

	// Render
	return (
		<PhotoCard
			{...props}
			render={
				<Button
					variant='ghost'
					aria-label={String(props.title)}
					data-active={active || undefined}
					disabled={props.disabled}
					isLoading={props.isLoading}
					onClick={props.onClick}
					onFocus={props.onFocus}
					onMouseEnter={props.onMouseEnter}
					className={rootClassName}
				/>
			}
		/>
	);
};

export type { PhotoBtnCardProps, PhotoCardProps };
